import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './EditJob.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const EditJob = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showCustomPort, setShowCustomPort] = useState(false);
  const [showCustomService, setShowCustomService] = useState(false);
  const [showCustomClass, setShowCustomClass] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    serviceType: '',
    customService: '',
    description: '',
    port: '',
    customPort: '',
    deadline: '',
    budgetType: 'fixed',
    budgetMin: '',
    budgetMax: '',
    currency: 'USD',
    classApproval: '',
    customClassApproval: ''
  });

  const serviceTypes = [
    'GMDSS Annual Survey', 'GMDSS Repair', 'Radio Survey', 'SBM Maintenance', 
    'Battery Replacement', 'Gyro Compass Repair', 'Radar Calibration',
    'AIS Service', 'ECDIS Service', 'EPIRB Testing', 'SART Testing',
    'NAVTEX Service', 'Safety Equipment Inspection', 'Navigation Systems',
    'Technical Spares Supply', 'VSAT & Communication', 'Crane & Winch Service',
    'HVAC & Refrigeration', 'Electrical Engineering', 'Main Engine Overhaul',
    'Hydraulic Systems', 'Ballast Water Systems', 'Tank Cleaning',
    'Lifeboat & Davit Service', 'Fire Fighting Equipment', 'Pest Control',
    'Crew Change Coordination', 'Customs Brokerage', 'Port Agency Services',
    'Ship Chandling', 'Bunkering Coordination', 'Marine Lubricants',
    'Hull Cleaning & Diving', 'Propeller Polishing', 'Underwater Inspection',
    'Medical & Healthcare', 'Lube Oil Analysis', 'Logistics & Storage',
    'Others'
  ];
  
  const ports = [
    'Dubai, UAE', 'Abu Dhabi, UAE', 'Sharjah, UAE', 'Fujairah, UAE', 
    'Jebel Ali, UAE', 'Khor Fakkan, UAE', 'Ras Al Khaimah, UAE',
    'Jeddah, Saudi Arabia', 'Dammam, Saudi Arabia', 'Yanbu, Saudi Arabia',
    'Doha, Qatar', 'Ras Laffan, Qatar', 'Manama, Bahrain', 'Sitra, Bahrain',
    'Muscat, Oman', 'Salalah, Oman', 'Sohar, Oman', 'Duqm, Oman',
    'Kuwait City, Kuwait', 'Shuwaikh, Kuwait',
    'Singapore', 'Hong Kong', 'Shanghai, China', 'Busan, South Korea',
    'Tokyo, Japan', 'Yokohama, Japan', 'Kaohsiung, Taiwan',
    'Jakarta, Indonesia', 'Port Klang, Malaysia', 'Bangkok, Thailand',
    'Ho Chi Minh City, Vietnam', 'Manila, Philippines',
    'Colombo, Sri Lanka', 'Chittagong, Bangladesh',
    'Mumbai, India', 'Chennai, India', 'Kolkata, India',
    'Karachi, Pakistan', 'Gwadar, Pakistan',
    'Lagos, Nigeria', 'Tema, Ghana', 'Abidjan, Ivory Coast',
    'Dakar, Senegal', 'Mombasa, Kenya', 'Dar es Salaam, Tanzania',
    'Durban, South Africa', 'Cape Town, South Africa',
    'Alexandria, Egypt', 'Port Said, Egypt', 'Suez, Egypt',
    'Casablanca, Morocco', 'Tunis, Tunisia', 'Algiers, Algeria',
    'Rotterdam, Netherlands', 'Antwerp, Belgium', 'Hamburg, Germany',
    'London, UK', 'Southampton, UK', 'Liverpool, UK',
    'Marseille, France', 'Barcelona, Spain', 'Valencia, Spain',
    'Genoa, Italy', 'Piraeus, Greece', 'Istanbul, Turkey',
    'Constanta, Romania', 'Odessa, Ukraine',
    'Houston, USA', 'New York, USA', 'Los Angeles, USA',
    'Miami, USA', 'New Orleans, USA', 'Vancouver, Canada',
    'Santos, Brazil', 'Buenos Aires, Argentina',
    'Panama Canal, Panama', 'Kingston, Jamaica',
    'Others'
  ];

  const classApprovals = ['DNV', 'LR', 'ABS', 'BV', 'NK', 'RINA', 'CCS', 'KR', 'IRS', 'Others'];

  useEffect(() => {
    const fetchJobDetails = async () => {
      try {
        const res = await fetch(`${API_BASE}/rfqs/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.success && data.rfq) {
          const rfq = data.rfq;
          
          const isStandardService = serviceTypes.includes(rfq.serviceType);
          const isStandardPort = ports.includes(rfq.port);
          const isStandardClass = !rfq.classApproval || classApprovals.includes(rfq.classApproval);

          setFormData({
            serviceType: isStandardService ? rfq.serviceType : 'Others',
            customService: isStandardService ? '' : rfq.serviceType,
            description: rfq.description || '',
            port: isStandardPort ? rfq.port : 'Others',
            customPort: isStandardPort ? '' : rfq.port,
            deadline: rfq.deadline ? new Date(rfq.deadline).toISOString().split('T')[0] : '',
            budgetType: rfq.budgetType || 'fixed',
            budgetMin: rfq.budgetMin || rfq.budget || '',
            budgetMax: rfq.budgetMax || '',
            currency: rfq.currency || 'USD',
            classApproval: isStandardClass ? (rfq.classApproval || '') : 'Others',
            customClassApproval: isStandardClass ? '' : rfq.classApproval
          });

          setShowCustomService(!isStandardService);
          setShowCustomPort(!isStandardPort);
          setShowCustomClass(!isStandardClass);
        }
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };
    if (id && token) fetchJobDetails();
  }, [id, token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    
    if (name === 'port') {
      setShowCustomPort(value === 'Others');
      if (value !== 'Others') setFormData(prev => ({ ...prev, customPort: '' }));
    }
    if (name === 'serviceType') {
      setShowCustomService(value === 'Others');
      if (value !== 'Others') setFormData(prev => ({ ...prev, customService: '' }));
    }
    if (name === 'classApproval') {
      setShowCustomClass(value === 'Others');
      if (value !== 'Others') setFormData(prev => ({ ...prev, customClassApproval: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.port) newErrors.port = 'Vessel port is required';
    if (formData.port === 'Others' && !formData.customPort.trim()) newErrors.port = 'Please specify custom port';
    if (!formData.serviceType) newErrors.serviceType = 'Service type is required';
    if (formData.serviceType === 'Others' && !formData.customService.trim()) newErrors.serviceType = 'Please specify custom service';
    if (formData.classApproval === 'Others' && !formData.customClassApproval.trim()) newErrors.classApproval = 'Please specify class society';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    
    if (formData.budgetType !== 'quotation') {
      if (!formData.budgetMin || parseFloat(formData.budgetMin) <= 0) newErrors.budgetMin = 'Valid amount is required';
      if (!formData.budgetMax || parseFloat(formData.budgetMax) <= 0) newErrors.budgetMax = 'Valid amount is required';
      if (parseFloat(formData.budgetMin) > parseFloat(formData.budgetMax)) {
        newErrors.budgetMax = 'Max must be greater than min';
      }
    }
    
    if (!formData.deadline) newErrors.deadline = 'Deadline is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSaving(true);
    try {
      const finalServiceType = formData.serviceType === 'Others' ? formData.customService.trim() : formData.serviceType;
      const finalPort = formData.port === 'Others' ? formData.customPort.trim() : formData.port;
      const finalClass = formData.classApproval === 'Others' ? formData.customClassApproval.trim() : formData.classApproval;

      const payload = {
        port: finalPort,
        serviceType: finalServiceType,
        classApproval: finalClass || undefined,
        description: formData.description,
        budgetMin: formData.budgetType === 'quotation' ? undefined : parseFloat(formData.budgetMin),
        budgetMax: formData.budgetType === 'quotation' ? undefined : parseFloat(formData.budgetMax),
        currency: formData.currency,
        budgetType: formData.budgetType,
        deadline: formData.deadline
      };

      const res = await fetch(`${API_BASE}/rfqs/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        navigate('/owner/my-jobs-quotations');
      }
    } catch (err) {
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this job? All associated quotations will be lost.')) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API_BASE}/rfqs/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        navigate('/owner/my-jobs-quotations');
      }
    } catch (err) {
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return (
    <div className="loader-container">
      <i className="fa-solid fa-spinner fa-spin"></i>
      <span>Loading details...</span>
    </div>
  );

  return (
    <div className="ej-wrapper">
      <div className="ej-header">
        <div className="ej-header-text">
          <h1><i className="fa-solid fa-pen-to-square"></i> Edit Job Requirements</h1>
          <p>Modify the details of your posted service request</p>
        </div>
        <button className="ej-btn-back" onClick={() => navigate(-1)}>
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="ej-section">
          <h2 className="ej-section-title"><i className="fa-solid fa-list-check"></i> Requirement Details</h2>
          
          <div className="ej-grid">
            <div className="ej-input-group">
              <label className="ej-label">Service Type</label>
              <select className="ej-input" name="serviceType" value={formData.serviceType} onChange={handleChange}>
                <option value="">Select service type</option>
                {serviceTypes.map(service => <option key={service} value={service}>{service}</option>)}
              </select>
              {errors.serviceType && <span className="ej-error-text">{errors.serviceType}</span>}
            </div>

            <div className="ej-input-group">
              <label className="ej-label">Vessel Port</label>
              <select className="ej-input" name="port" value={formData.port} onChange={handleChange}>
                <option value="">Select current port</option>
                {ports.map(port => <option key={port} value={port}>{port}</option>)}
              </select>
              {errors.port && <span className="ej-error-text">{errors.port}</span>}
            </div>

            {showCustomService && (
              <div className="ej-input-group">
                <label className="ej-label">Specify Service Type</label>
                <input className="ej-input" type="text" name="customService" value={formData.customService} onChange={handleChange} placeholder="Enter custom service..." />
              </div>
            )}

            {showCustomPort && (
              <div className="ej-input-group">
                <label className="ej-label">Specify Port</label>
                <input className="ej-input" type="text" name="customPort" value={formData.customPort} onChange={handleChange} placeholder="Enter port, city, country..." />
              </div>
            )}

            <div className="ej-input-group">
              <label className="ej-label">Class Approval (Optional)</label>
              <select className="ej-input" name="classApproval" value={formData.classApproval} onChange={handleChange}>
                <option value="">No specific class required</option>
                {classApprovals.map(cls => <option key={cls} value={cls}>{cls}</option>)}
              </select>
            </div>
            
            {showCustomClass && (
              <div className="ej-input-group">
                <label className="ej-label">Specify Class Society</label>
                <input className="ej-input" type="text" name="customClassApproval" value={formData.customClassApproval} onChange={handleChange} placeholder="Enter class society..." />
              </div>
            )}
          </div>

          <div className="ej-grid ej-grid-full" style={{ marginTop: '20px' }}>
            <div className="ej-input-group">
              <label className="ej-label">Scope of Work</label>
              <textarea className="ej-input" name="description" value={formData.description} onChange={handleChange} placeholder="Provide detailed requirements..." />
              {errors.description && <span className="ej-error-text">{errors.description}</span>}
            </div>
          </div>
        </div>

        <div className="ej-section">
          <h2 className="ej-section-title"><i className="fa-solid fa-money-bill-wave"></i> Budget & Timeline</h2>
          
          <div className="ej-grid">
            <div className="ej-input-group">
              <label className="ej-label">Budget Type</label>
              <select className="ej-input" name="budgetType" value={formData.budgetType} onChange={handleChange}>
                <option value="fixed">Fixed Price</option>
                <option value="hourly">Per Hour</option>
                <option value="quotation">Request Quotation Only</option>
              </select>
            </div>

            {/* Currency - ALWAYS visible */}
            <div className="ej-input-group">
              <label className="ej-label">Currency</label>
              <select className="ej-input" name="currency" value={formData.currency} onChange={handleChange}>
                <option value="USD">USD - US Dollar</option>
                <option value="EUR">EUR - Euro</option>
                <option value="GBP">GBP - British Pound</option>
              </select>
            </div>

            <div className="ej-input-group">
              <label className="ej-label">Deadline</label>
              <input className="ej-input" type="date" name="deadline" value={formData.deadline} onChange={handleChange} min={new Date().toISOString().split('T')[0]} />
              {errors.deadline && <span className="ej-error-text">{errors.deadline}</span>}
            </div>

            {formData.budgetType !== 'quotation' && (
              <>
                <div className="ej-input-group">
                  <label className="ej-label">Minimum Amount</label>
                  <div className="ej-budget-wrapper">
                    <span className="ej-input ej-currency" style={{ display: 'inline-flex', alignItems: 'center', background: '#111', padding: '0 12px', borderRadius: '4px', color: '#fff' }}>
                      {formData.currency}
                    </span>
                    <input className="ej-input" type="number" name="budgetMin" value={formData.budgetMin} onChange={handleChange} placeholder="0.00" min="0" />
                  </div>
                  {errors.budgetMin && <span className="ej-error-text">{errors.budgetMin}</span>}
                </div>
                
                <div className="ej-input-group">
                  <label className="ej-label">Maximum Amount</label>
                  <div className="ej-budget-wrapper">
                    <span className="ej-input ej-currency" style={{ display: 'inline-flex', alignItems: 'center', background: '#111', padding: '0 12px', borderRadius: '4px', color: '#fff' }}>
                      {formData.currency}
                    </span>
                    <input className="ej-input" type="number" name="budgetMax" value={formData.budgetMax} onChange={handleChange} placeholder="0.00" min="0" />
                  </div>
                  {errors.budgetMax && <span className="ej-error-text">{errors.budgetMax}</span>}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="ej-actions">
          <button type="button" className="ej-btn ej-btn-danger" onClick={handleDelete} disabled={deleting || saving}>
            <i className="fa-solid fa-trash-can"></i> {deleting ? 'Deleting...' : 'Delete Job'}
          </button>
          <div className="ej-actions-right">
            <button type="button" className="ej-btn ej-btn-secondary" onClick={() => navigate('/owner/my-jobs-quotations')} disabled={saving || deleting}>
              Cancel
            </button>
            <button type="submit" className="ej-btn ej-btn-primary" disabled={saving || deleting}>
              {saving ? <><i className="fa-solid fa-spinner fa-spin"></i> Saving...</> : 'Save Changes'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditJob;